import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Icon, useTheme } from "@outschool/backpack";
import { faArrowRight, faQuoteLeft } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import teacherImg from "../../../../apps/website/app/images/Illustration_Teacher_200x200-Color.png";
import pencilImg from "../../../../apps/website/app/images/Illustration_Pencil_200x200-Color.png";
import starImg from "../../../../apps/website/app/images/Illustration_Star_200x200-Color.png";
import scienceImg from "../../../../apps/website/app/images/Illustration_Science_200x200-Color.png";
import puzzleImg from "../../../../apps/website/app/images/Illustration_Puzzle_200x200-Color.png";

export const TESTIMONIALS = [
  {
    testimonialText: `The flexibility of Outschool has been a game-changer. The instructors have a remarkable ability to make learning fun and interactive, keeping our child fully engaged throughout the lessons.`,
    authorName: "Xaviera",
    avatar: teacherImg,
  },
  {
    testimonialText: `Outschool has something for everyone. Whatever your kid's interest, there is likely an available class, a teacher willing to create something, or include it in an existing class.`,
    authorName: "Amy",
    avatar: pencilImg,
  },
  {
    testimonialText: `My daughter loves her Outschool classes and they are incredibly convenient for us as a homeschool family. The teachers are amazing!`,
    authorName: "Audra",
    avatar: puzzleImg,
  },
  {
    testimonialText: `Variety of classes, expert teachers, classes for introverted students or students looking for social settings. Both core classes and fun electives.`,
    authorName: "Shauna",
    avatar: starImg,
  },
  {
    testimonialText: `Our Outschool classes are always so fun and my son is learning so much! He loves his scratch coding class and feels a real sense of accomplishment each week. All of the teachers have been so kind and patient. Money well spent!`,
    authorName: "Silas",
    avatar: scienceImg,
  },
];

export default function Testimonial() {
  const { t } = useTranslation(
    "ui-components-website\\Testimonials\\TestimonialsCarousel"
  );

  const theme = useTheme();

  const carouselRef = useRef<HTMLDivElement | null>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    }
  };

  useEffect(() => {
    const currentRef = carouselRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const scrollRight = () => {
    if (!carouselRef.current) {
      return;
    }

    const container = carouselRef.current;
    const cardWidth = 315;
    const scrollPosition = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    if (scrollPosition >= maxScroll - 1) {
      setTimeout(() => {
        container.scrollTo({
          left: 0,
          behavior: "smooth",
        });
        setIsAtEnd(false);
      }, 300);
    } else {
      container.scrollBy({
        left: cardWidth,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F0F2FF",
        margin: "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "50px",
      }}
    >
      <Box
        sx={{ maxWidth: "1300px", textAlign: "center", position: "relative" }}
      >
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              [theme.breakpoints.down("md")]: {
                display: "none",
              },
              position: "absolute",
              top: "12px",
              right: "10px",
              width: "22px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #4B01D4",
              borderRadius: "50%",
              backgroundColor: "transparent",
              "&:hover": {
                border: "2px solid #4B01D4",
              },
            }}
            onClick={scrollRight}
          >
            <Icon
              icon={faArrowRight}
              sx={{
                width: 18,
                height: 18,
                color: "#4B01D4",
              }}
            />
          </Box>
        </Box>
        <Typography variant="h2">
          {t("Trusted by 1+ million families")}
        </Typography>
        <Box sx={{ position: "relative" }}>
          {!isAtStart && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "120px",
                height: "100%",
                background:
                  "linear-gradient(to right, rgba(240,242,255,1), transparent)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "30px",
            }}
          >
            <Box
              ref={carouselRef}
              sx={{
                display: "flex",
                gap: "10px",
                overflowX: "auto",
                scrollBehavior: "smooth",
                scrollbarWidth: "none",
                paddingBottom: "10px",
                whiteSpace: "nowrap",
                maxWidth: "90vw",
                position: "relative",
              }}
            >
              {TESTIMONIALS.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "0 0 305px",
                    [theme.breakpoints.down("md")]: {
                      flex: "0 0 260px",
                    },
                    padding: "15px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Icon
                    icon={faQuoteLeft}
                    sx={{
                      width: 40,
                      height: 40,
                      marginBottom: "30px",
                      color: "#5A00E0",
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: "10px",
                      whiteSpace: "normal",
                    }}
                  >
                    {item.testimonialText}
                  </Typography>
                  <Box
                    flex
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ marginRight: "8px" }}>
                      {item.avatar && (
                        <img
                          src={item.avatar.src ?? item.avatar}
                          style={{
                            width: "60px",
                            borderRadius: "50%",
                            display: "block",
                          }}
                          alt={item.authorName}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="h6">{item.authorName}</Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "var(--text-subdue, #5C5C5C);" }}
                      >
                        {t("Parent")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          {!isAtEnd && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "120px",
                height: "100%",
                background:
                  "linear-gradient(to left, rgba(240,242,255,1), transparent)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
